import { api, eur } from "@/lib/api";
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
  ComposedChart, Line, CartesianGrid,
} from "recharts";
import {
  TrendingUp, TrendingDown, ShoppingBag, Wallet, Utensils, Clock, Timer,
  ChefHat, Users, CreditCard, AlertTriangle, ArrowRight, Flame, Star,
} from "lucide-react";

const RANGES = [
  { key: "today", label: "Aujourd'hui" },
  { key: "yesterday", label: "Hier" },
  { key: "7d", label: "7 jours" },
  { key: "30d", label: "30 jours" },
];

const INSUFF = "Données insuffisantes";

const fmtMin = (m) => {
  if (m === null || m === undefined) return null;
  const h = Math.floor(m / 60), mm = Math.round(m % 60);
  return h > 0 ? `${h} h ${String(mm).padStart(2, "0")}` : `${mm} min`;
};
const fmtPrep = (s) => {
  if (s === null || s === undefined) return null;
  const m = Math.floor(s / 60), ss = Math.round(s % 60);
  return m > 0 ? `${m} min ${String(ss).padStart(2, "0")} s` : `${ss} s`;
};

function Delta({ pct, invert }) {
  if (pct === null || pct === undefined) return <span className="text-[11px] text-stone-600">— vs période préc.</span>;
  const positive = invert ? pct < 0 : pct > 0;
  const flat = pct === 0;
  const Icon = pct >= 0 ? TrendingUp : TrendingDown;
  const color = flat ? "text-stone-500" : positive ? "text-emerald-400" : "text-red-400";
  return (
    <span className={`text-[11px] font-semibold inline-flex items-center gap-1 ${color}`}>
      <Icon className="w-3 h-3" /> {pct > 0 ? "+" : ""}{pct}% <span className="text-stone-600 font-normal">vs préc.</span>
    </span>
  );
}

function Kpi({ icon: Icon, label, value, delta, invert, testid, insufficient, onClick }) {
  const clickable = !!onClick && !insufficient;
  return (
    <div data-testid={testid} onClick={clickable ? onClick : undefined}
      className={`rounded-2xl bg-[#111F1A] border border-[#1B3028] p-4 sm:p-5 transition-all ${clickable ? "cursor-pointer hover:border-[#C69C4A]/60 hover:bg-[#132420] active:scale-[0.99]" : ""}`}>
      <div className="w-9 h-9 rounded-xl bg-[#1A3D32] grid place-items-center mb-3">
        <Icon className="w-4.5 h-4.5 text-[#C69C4A]" />
      </div>
      <p className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">{label}</p>
      <p className={`font-bold text-stone-100 ${insufficient ? "text-sm text-stone-500 font-normal" : "text-2xl"}`}>
        {insufficient ? INSUFF : value}
      </p>
      {delta !== undefined && !insufficient && <div className="mt-1.5"><Delta pct={delta} invert={invert} /></div>}
      {clickable && <p className="mt-2 text-[11px] font-semibold text-[#C69C4A] inline-flex items-center gap-1">Voir le détail <ArrowRight className="w-3 h-3" /></p>}
    </div>
  );
}

const fmtHM = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };
const BD_TITLE = {
  waiter_requests: { title: "Demandes serveur", sub: "Demandes clients enregistrées sur la période (données réelles)" },
  pending_payments: { title: "Paiements en attente", sub: "Tables ouvertes avec un reste à encaisser (temps réel)" },
  satisfaction: { title: "Satisfaction client", sub: "Avis internes déposés sur la période" },
  upsells: { title: "Upsells attribués", sub: "Recommandations proposées puis réellement achetées" },
  roi: { title: "Performance & ROI NOVANEXT", sub: "Valeurs mesurées réellement + estimation explicite du ROI" },
};
const METRIC = {
  revenue: { title: "CA généré", sub: "Commandes composant le chiffre d'affaires", head: (d) => `Total : ${eur(d.revenue)} · ${d.count} commande${d.count > 1 ? "s" : ""}` },
  orders: { title: "Commandes", sub: "Toutes les commandes de la période", head: (d) => `${d.count} commande${d.count > 1 ? "s" : ""} · ${eur(d.revenue)}` },
  avg: { title: "Panier moyen", sub: "Calcul : CA ÷ nombre de commandes", head: (d) => `${eur(d.avg_ticket)}  =  ${eur(d.revenue)} ÷ ${d.count}` },
  prep: { title: "Temps de préparation", sub: "Commandes avec départ et fin de préparation", head: (d) => `${d.orders.filter((o) => o.prep_seconds != null).length} commande(s) mesurée(s)` },
};

function LiveTile({ dot, count, label, onClick, testid, urgent }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-left rounded-xl border p-3.5 transition-all hover:border-[#C69C4A]/60 active:scale-[0.98] ${urgent && count > 0 ? "border-red-500/40 bg-red-500/[0.06]" : "border-[#1B3028] bg-[#0D1814]"}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-2xl font-bold text-stone-100">{count}</span>
        <span className="text-base">{dot}</span>
      </div>
      <p className="text-[11px] text-stone-400 mt-1 leading-tight">{label}</p>
    </button>
  );
}

const fmtWait = (s) => {
  if (s === null || s === undefined) return "—";
  const m = Math.floor(s / 60), ss = Math.round(s % 60);
  return m > 0 ? `${m} min ${String(ss).padStart(2, "0")} s` : `${ss} s`;
};

export default function StatsPanel({ slug, stats, range, onRange, onNavigate, reviewSummary }) {
  const [showCustom, setShowCustom] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [detail, setDetail] = useState(null); // { metric, loading, data }
  const [bd, setBd] = useState(null); // breakdown drill-down { metric, loading, data }
  const [roiCost, setRoiCost] = useState(() => localStorage.getItem("novanext_roi_cost") || "");
  const [impact, setImpact] = useState({ upsells: null, roi: null });

  const rk = range?.key || "today";
  const rFrom = range?.from || null;
  const rTo = range?.to || null;
  useEffect(() => {
    if (!slug) return;
    const p = { range: rk };
    if (rFrom) p.from = rFrom;
    if (rTo) p.to = rTo;
    let alive = true;
    Promise.all([
      api.statsBreakdown(slug, { ...p, metric: "upsells" }),
      api.statsBreakdown(slug, { ...p, metric: "roi" }),
    ]).then(([u, r]) => { if (alive) setImpact({ upsells: u, roi: r }); }).catch(() => {});
    return () => { alive = false; };
  }, [slug, rk, rFrom, rTo]);

  if (!stats || !stats.kpis) {
    return <div className="rounded-2xl border border-dashed border-[#1B3028] py-16 text-center text-stone-500">Chargement des statistiques…</div>;
  }
  const k = stats.kpis;
  const live = stats.live || {};
  const activity = stats.activity_by_hour || [];
  const peak = stats.peak_hour;
  const rangeKey = range?.key || "today";

  const nav = (tab) => onNavigate && onNavigate(tab);

  const openDetail = (metric) => {
    if (!slug) return;
    setDetail({ metric, loading: true, data: null });
    const params = { range: rangeKey };
    if (range?.from) params.from = range.from;
    if (range?.to) params.to = range.to;
    api.statsDetail(slug, params)
      .then((data) => setDetail({ metric, loading: false, data }))
      .catch(() => setDetail({ metric, loading: false, data: null }));
  };

  const openBreakdown = (metric) => {
    if (!slug) return;
    setBd({ metric, loading: true, data: null });
    const params = { metric, range: rangeKey };
    if (range?.from) params.from = range.from;
    if (range?.to) params.to = range.to;
    api.statsBreakdown(slug, params)
      .then((data) => setBd({ metric, loading: false, data }))
      .catch(() => setBd({ metric, loading: false, data: null }));
  };

  return (
    <div className="space-y-6">
      {/* Period filter bar */}
      <div className="flex flex-wrap items-center gap-2" data-testid="stats-period-bar">
        {RANGES.map((r) => (
          <button
            key={r.key}
            data-testid={`stats-range-${r.key}`}
            onClick={() => { setShowCustom(false); onRange({ key: r.key, from: null, to: null }); }}
            className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-all ${
              rangeKey === r.key && !showCustom ? "bg-[#C69C4A] text-[#0C1713] border-[#C69C4A]" : "bg-[#111F1A] text-stone-300 border-[#1B3028] hover:border-[#C69C4A]/50"
            }`}
          >
            {r.label}
          </button>
        ))}
        <button
          data-testid="stats-range-custom"
          onClick={() => setShowCustom((v) => !v)}
          className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider border transition-all ${
            rangeKey === "custom" || showCustom ? "bg-[#C69C4A] text-[#0C1713] border-[#C69C4A]" : "bg-[#111F1A] text-stone-300 border-[#1B3028] hover:border-[#C69C4A]/50"
          }`}
        >
          Personnalisé
        </button>
        {showCustom && (
          <div className="flex items-center gap-2 flex-wrap">
            <input type="date" data-testid="stats-custom-from" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="rounded-lg bg-[#111F1A] border border-[#1B3028] px-3 py-1.5 text-xs text-stone-100" />
            <span className="text-stone-500 text-xs">→</span>
            <input type="date" data-testid="stats-custom-to" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="rounded-lg bg-[#111F1A] border border-[#1B3028] px-3 py-1.5 text-xs text-stone-100" />
            <button
              data-testid="stats-custom-apply"
              disabled={!customFrom || !customTo}
              onClick={() => onRange({ key: "custom", from: customFrom, to: customTo })}
              className="rounded-lg bg-[#1A3D32] border border-[#2A5344] px-3 py-1.5 text-xs font-semibold text-stone-100 disabled:opacity-40"
            >
              Appliquer
            </button>
          </div>
        )}
      </div>

      {/* NIVEAU 1 — KPI */}
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-stone-500 mb-2 px-1">Indicateurs clés · Historique</p>
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
          <Kpi testid="stat-revenue" icon={Wallet} label="CA généré" value={eur(k.revenue.value)} delta={k.revenue.delta_pct} onClick={() => openDetail("revenue")} />
          <Kpi testid="stat-count" icon={ShoppingBag} label="Commandes" value={k.orders.value} delta={k.orders.delta_pct} onClick={() => openDetail("orders")} />
          <Kpi testid="stat-avg" icon={TrendingUp} label="Panier moyen" value={eur(k.avg_ticket.value)} delta={k.avg_ticket.delta_pct} onClick={() => openDetail("avg")} />
          <Kpi testid="stat-tables" icon={Utensils} label="Tables actives" value={k.active_tables.value} onClick={() => nav("tables")} />
          <Kpi testid="stat-table-time" icon={Clock} label="Temps moyen / table" value={fmtMin(k.avg_table_minutes.value)} delta={k.avg_table_minutes.delta_pct} invert insufficient={k.avg_table_minutes.value === null} onClick={() => nav("tables")} />
          <Kpi testid="stat-prep-time" icon={Timer} label="Temps moyen prépa" value={fmtPrep(k.avg_prep_seconds.value)} delta={k.avg_prep_seconds.delta_pct} invert insufficient={k.avg_prep_seconds.value === null} onClick={() => openDetail("prep")} />
        </div>
      </div>

      {/* KPI drill-down modal — données réelles réconciliées avec le KPI */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="stat-detail-modal" className="max-w-2xl bg-[#0C1713] border border-[#1B3028] text-stone-100">
          {detail && (
            <>
              <DialogTitle className="text-lg text-stone-100">{METRIC[detail.metric]?.title}</DialogTitle>
              <DialogDescription className="text-stone-500 text-xs">{METRIC[detail.metric]?.sub}</DialogDescription>
              {detail.loading ? (
                <div className="py-10 text-center text-stone-500 text-sm">Chargement…</div>
              ) : !detail.data || detail.data.count === 0 ? (
                <div data-testid="stat-detail-empty" className="py-10 text-center text-stone-500 text-sm">Aucune commande sur cette période.</div>
              ) : (
                <>
                  <div data-testid="stat-detail-total" className="mt-1 mb-3 rounded-xl bg-[#111F1A] border border-[#1B3028] px-4 py-3 text-sm font-semibold text-[#E7C97A]">
                    {METRIC[detail.metric]?.head(detail.data)}
                  </div>
                  <div className="max-h-[50vh] overflow-y-auto rounded-xl border border-[#1B3028] divide-y divide-[#132420]">
                    {detail.data.orders.map((o) => (
                      <div key={o.id} data-testid="stat-detail-row" className="flex items-center gap-3 px-3 py-2.5 text-sm">
                        <span className="font-mono text-xs text-stone-400 w-32 shrink-0">{o.order_code}</span>
                        <span className="text-stone-300 w-20 shrink-0">Table {o.table_number}</span>
                        <span className="text-stone-500 text-xs w-24 shrink-0">{fmtHM(o.created_at)}</span>
                        <span className="text-stone-500 text-xs flex-1">{o.items_count} art.{o.guest_name ? ` · ${o.guest_name}` : ""}{detail.metric === "prep" ? ` · ${o.prep_seconds != null ? fmtPrep(o.prep_seconds) : "—"}` : ""}</span>
                        <span className="font-bold text-stone-100 w-20 text-right shrink-0">{eur(o.total)}</span>
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-[11px] text-stone-600">Ces lignes proviennent des commandes réellement enregistrées et composent exactement la valeur affichée du KPI.</p>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Satisfaction — avis clients */}
      <div className="rounded-2xl bg-[#111F1A] border border-[#1B3028] p-5" data-testid="stats-review-widget">
        <div className="flex items-center gap-2 mb-3">
          <Star className="w-4 h-4 text-[#C69C4A]" />
          <h3 className="text-sm font-bold uppercase tracking-wider text-stone-200">Satisfaction client</h3>
          <button
            data-testid="review-widget-detail"
            onClick={() => openBreakdown("satisfaction")}
            className="ml-auto text-[11px] font-semibold text-[#C69C4A] inline-flex items-center gap-1 hover:underline"
          >
            Voir les avis <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        {reviewSummary && reviewSummary.count > 0 ? (
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center gap-3">
              <span data-testid="review-widget-average" className="font-serif-display text-4xl text-white leading-none">{reviewSummary.average?.toFixed(1)}</span>
              <div>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className="w-4 h-4" style={{ color: "#C69C4A", fill: (reviewSummary.average || 0) >= n - 0.25 ? "#C69C4A" : "transparent" }} />
                  ))}
                </div>
                <p data-testid="review-widget-count" className="text-[11px] text-stone-500 mt-1">{reviewSummary.count} avis client{reviewSummary.count > 1 ? "s" : ""}</p>
              </div>
            </div>
            <div className="flex-1 min-w-[180px] space-y-1">
              {[5, 4, 3, 2, 1].map((n) => {
                const c = reviewSummary.distribution?.[String(n)] || 0;
                const pct = reviewSummary.count ? Math.round((c / reviewSummary.count) * 100) : 0;
                return (
                  <div key={n} className="flex items-center gap-2 text-[11px] text-stone-400">
                    <span className="w-3">{n}</span><Star className="w-3 h-3 text-[#C69C4A]" />
                    <div className="flex-1 h-1.5 rounded-full bg-[#0A1712] overflow-hidden"><div className="h-full bg-[#C69C4A]" style={{ width: `${pct}%` }} /></div>
                    <span className="w-6 text-right">{c}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <p data-testid="review-widget-empty" className="text-sm text-stone-500">Aucun avis client pour le moment. Activez la demande d'avis dans Paramètres → Avis clients.</p>
        )}
      </div>

      {/* IMPACT NOVANEXT — upsells + ROI (données réelles + estimation explicite) */}
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-stone-500 mb-2 px-1">Impact NOVANEXT</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div data-testid="impact-upsells" onClick={() => openBreakdown("upsells")}
            className="rounded-2xl bg-[#111F1A] border border-[#1B3028] p-4 sm:p-5 cursor-pointer transition-all hover:border-[#C69C4A]/60 hover:bg-[#132420] active:scale-[0.99]">
            <div className="w-9 h-9 rounded-xl bg-[#1A3D32] grid place-items-center mb-3"><Flame className="w-4.5 h-4.5 text-[#C69C4A]" /></div>
            <p className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">Upsells attribués</p>
            <p className="font-bold text-2xl text-stone-100">{impact.upsells ? eur(impact.upsells.attributed_value) : "…"}</p>
            {impact.upsells && (
              <p className="text-[11px] text-stone-500 mt-1">{impact.upsells.purchased} acheté{impact.upsells.purchased > 1 ? "s" : ""} · {impact.upsells.shown} proposé{impact.upsells.shown > 1 ? "s" : ""}{impact.upsells.conversion_pct != null ? ` · ${impact.upsells.conversion_pct}%` : ""}</p>
            )}
            <p className="mt-2 text-[11px] font-semibold text-[#C69C4A] inline-flex items-center gap-1">Voir le détail <ArrowRight className="w-3 h-3" /></p>
          </div>
          <div data-testid="impact-roi" onClick={() => openBreakdown("roi")}
            className="rounded-2xl bg-[#111F1A] border border-[#1B3028] p-4 sm:p-5 cursor-pointer transition-all hover:border-[#C69C4A]/60 hover:bg-[#132420] active:scale-[0.99]">
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-xl bg-[#1A3D32] grid place-items-center"><TrendingUp className="w-4.5 h-4.5 text-[#C69C4A]" /></div>
              <span className="text-[9px] font-bold uppercase tracking-widest text-amber-400 bg-amber-400/10 border border-amber-400/30 rounded-full px-2 py-0.5">Estimation</span>
            </div>
            <p className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">CA attribuable NOVANEXT</p>
            <p className="font-bold text-2xl text-stone-100">{impact.roi ? eur((impact.roi.qr_revenue || 0)) : "…"}</p>
            {impact.roi && (
              <p className="text-[11px] text-stone-500 mt-1">{impact.roi.qr_orders} cmd QR · {impact.roi.autonomous_payments} paiement{impact.roi.autonomous_payments > 1 ? "s" : ""} autonome{impact.roi.autonomous_payments > 1 ? "s" : ""}</p>
            )}
            <p className="mt-2 text-[11px] font-semibold text-[#C69C4A] inline-flex items-center gap-1">Voir le détail <ArrowRight className="w-3 h-3" /></p>
          </div>
        </div>
      </div>

      {/* Drill-down breakdown modal — waiter requests / pending payments / satisfaction / upsells / ROI */}
      <Dialog open={!!bd} onOpenChange={(o) => !o && setBd(null)}>
        <DialogContent data-testid="stat-breakdown-modal" className="max-w-2xl bg-[#0C1713] border border-[#1B3028] text-stone-100 max-h-[85vh] overflow-y-auto">
          {bd && (
            <>
              <DialogTitle className="text-lg text-stone-100">{BD_TITLE[bd.metric]?.title}</DialogTitle>
              <DialogDescription className="text-stone-500 text-xs">{BD_TITLE[bd.metric]?.sub}</DialogDescription>
              {bd.loading ? (
                <div className="py-10 text-center text-stone-500 text-sm">Chargement…</div>
              ) : !bd.data ? (
                <div data-testid="breakdown-empty" className="py-10 text-center text-stone-500 text-sm">Données indisponibles.</div>
              ) : bd.metric === "waiter_requests" ? (
                <div className="space-y-3">
                  <div data-testid="breakdown-summary" className="rounded-xl bg-[#111F1A] border border-[#1B3028] px-4 py-3 text-sm font-semibold text-[#E7C97A]">
                    {bd.data.count} demande{bd.data.count > 1 ? "s" : ""} · {bd.data.handled} traitée{bd.data.handled > 1 ? "s" : ""} · {bd.data.pending} en attente · attente moyenne {fmtWait(bd.data.avg_wait_seconds)}
                  </div>
                  {bd.data.count === 0 ? <div className="py-8 text-center text-stone-500 text-sm">Aucune demande sur cette période.</div> : (
                    <div className="rounded-xl border border-[#1B3028] divide-y divide-[#132420]">
                      {bd.data.rows.map((r) => (
                        <div key={r.id} data-testid="breakdown-row" className="flex items-center gap-3 px-3 py-2.5 text-sm">
                          <span className="font-semibold text-stone-200 w-24 shrink-0">{r.label || r.type}</span>
                          <span className="text-stone-300 w-20 shrink-0">Table {r.table_number}</span>
                          <span className="text-stone-500 text-xs w-24 shrink-0">{fmtHM(r.created_at)}</span>
                          <span className="text-stone-500 text-xs flex-1">{r.handled ? `Traitée${r.handled_by ? ` · ${r.handled_by}` : ""}` : "En attente"}</span>
                          <span className={`text-xs font-semibold w-24 text-right shrink-0 ${r.handled ? "text-emerald-400" : "text-amber-400"}`}>{r.handled ? fmtWait(r.wait_seconds) : "…"}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : bd.metric === "pending_payments" ? (
                <div className="space-y-3">
                  <div data-testid="breakdown-summary" className="rounded-xl bg-[#111F1A] border border-[#1B3028] px-4 py-3 text-sm font-semibold text-[#E7C97A]">
                    {bd.data.count} table{bd.data.count > 1 ? "s" : ""} · reste à encaisser {eur(bd.data.total_remaining)}
                  </div>
                  {bd.data.count === 0 ? <div className="py-8 text-center text-stone-500 text-sm">Aucune table en attente de règlement.</div> : (
                    <div className="rounded-xl border border-[#1B3028] divide-y divide-[#132420]">
                      {bd.data.rows.map((r) => (
                        <div key={r.table_number} data-testid="breakdown-row" className="flex items-center gap-3 px-3 py-2.5 text-sm cursor-pointer hover:bg-[#111F1A]" onClick={() => { setBd(null); nav("tables"); }}>
                          <span className="font-semibold text-stone-200 w-24 shrink-0">Table {r.table_number}</span>
                          <span className="text-stone-500 text-xs flex-1">Addition {eur(r.bill_total)} · payé {eur(r.paid_amount)}{r.pending_amount > 0 ? ` · en attente ${eur(r.pending_amount)}` : ""}</span>
                          <span className="font-bold text-amber-400 w-24 text-right shrink-0">{eur(r.remaining)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : bd.metric === "satisfaction" ? (
                <div className="space-y-3">
                  <div data-testid="breakdown-summary" className="rounded-xl bg-[#111F1A] border border-[#1B3028] px-4 py-3 text-sm font-semibold text-[#E7C97A]">
                    {bd.data.count > 0 ? `Note moyenne ${bd.data.average} / 5 · ${bd.data.count} avis` : "Aucun avis sur cette période"}
                  </div>
                  {bd.data.count > 0 && (
                    <div className="grid grid-cols-5 gap-2 text-center text-xs">
                      {[5, 4, 3, 2, 1].map((n) => (
                        <div key={n} className="rounded-lg bg-[#111F1A] border border-[#1B3028] py-2">
                          <div className="text-stone-400">{n}★</div>
                          <div className="text-stone-100 font-bold">{bd.data.distribution?.[String(n)] || 0}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {bd.data.rows.length > 0 && (
                    <div className="rounded-xl border border-[#1B3028] divide-y divide-[#132420]">
                      {bd.data.rows.map((r, i) => (
                        <div key={i} data-testid="breakdown-row" className="px-3 py-2.5 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-[#C69C4A] font-bold">{"★".repeat(r.rating)}<span className="text-stone-700">{"★".repeat(5 - r.rating)}</span></span>
                            {r.table_number && <span className="text-stone-400 text-xs">Table {r.table_number}</span>}
                            <span className="text-stone-600 text-xs ml-auto">{fmtHM(r.created_at)}</span>
                          </div>
                          {r.comment && <p className="text-stone-300 text-xs mt-1 leading-snug">« {r.comment} »</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : bd.metric === "upsells" ? (
                <div className="space-y-3">
                  <div data-testid="breakdown-summary" className="rounded-xl bg-[#111F1A] border border-[#1B3028] px-4 py-3 text-sm font-semibold text-[#E7C97A]">
                    Valeur attribuée {eur(bd.data.attributed_value)} · {bd.data.purchased} achat{bd.data.purchased > 1 ? "s" : ""} / {bd.data.shown} proposé{bd.data.shown > 1 ? "s" : ""}{bd.data.conversion_pct != null ? ` · conversion ${bd.data.conversion_pct}%` : ""}
                  </div>
                  {bd.data.rows.length === 0 ? <div className="py-8 text-center text-stone-500 text-sm">Aucun upsell acheté sur cette période.</div> : (
                    <div className="rounded-xl border border-[#1B3028] divide-y divide-[#132420]">
                      {bd.data.rows.map((r, i) => (
                        <div key={i} data-testid="breakdown-row" className="flex items-center gap-3 px-3 py-2.5 text-sm">
                          <span className="font-semibold text-stone-200 flex-1">{r.product_name || "Produit"}</span>
                          {r.order_code && <span className="font-mono text-xs text-stone-500 shrink-0">{r.order_code}</span>}
                          {r.table_number && <span className="text-stone-400 text-xs w-16 shrink-0 text-right">T. {r.table_number}</span>}
                          <span className="font-bold text-stone-100 w-16 text-right shrink-0">{eur(r.price)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="text-[11px] text-stone-600">{bd.data.method}</p>
                </div>
              ) : bd.metric === "roi" ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-xl bg-[#111F1A] border border-[#1B3028] px-4 py-3"><p className="text-[11px] text-stone-500">CA via QR</p><p className="font-bold text-stone-100">{eur(bd.data.qr_revenue)}</p><p className="text-[11px] text-stone-600">{bd.data.qr_orders} commande{bd.data.qr_orders > 1 ? "s" : ""}</p></div>
                    <div className="rounded-xl bg-[#111F1A] border border-[#1B3028] px-4 py-3"><p className="text-[11px] text-stone-500">Paiements autonomes (carte)</p><p className="font-bold text-stone-100">{eur(bd.data.autonomous_amount)}</p><p className="text-[11px] text-stone-600">{bd.data.autonomous_payments} paiement{bd.data.autonomous_payments > 1 ? "s" : ""}</p></div>
                    <div className="rounded-xl bg-[#111F1A] border border-[#1B3028] px-4 py-3"><p className="text-[11px] text-stone-500">Upsells achetés</p><p className="font-bold text-stone-100">{eur(bd.data.upsell_attributed_value)}</p></div>
                    <div className="rounded-xl bg-[#111F1A] border border-[#1B3028] px-4 py-3">
                      <p className="text-[11px] text-stone-500 mb-1">Coût d'abonnement / période (€)</p>
                      <input data-testid="roi-cost-input" type="number" min="0" step="1" value={roiCost}
                        onChange={(e) => { setRoiCost(e.target.value); localStorage.setItem("novanext_roi_cost", e.target.value); }}
                        placeholder="ex : 99" className="w-full rounded-lg bg-[#0A1712] border border-[#1B3028] px-2 py-1.5 text-sm text-stone-100" />
                    </div>
                  </div>
                  {(() => {
                    const attributable = (bd.data.qr_revenue || 0) + (bd.data.upsell_attributed_value || 0);
                    const cost = parseFloat(roiCost);
                    const hasCost = !isNaN(cost) && cost >= 0;
                    const net = hasCost ? attributable - cost : null;
                    return (
                      <div data-testid="roi-net" className={`rounded-xl border px-4 py-3 text-sm ${hasCost ? (net >= 0 ? "border-emerald-500/40 bg-emerald-500/[0.06]" : "border-red-500/40 bg-red-500/[0.06]") : "border-[#1B3028] bg-[#111F1A]"}`}>
                        <p className="text-stone-400 text-xs">Valeur attribuable NOVANEXT (CA QR + upsells) : <span className="font-semibold text-stone-100">{eur(attributable)}</span></p>
                        {hasCost ? (
                          <p className="mt-1 font-bold text-base">{net >= 0 ? "ROI estimé positif : +" : "ROI estimé : "}{eur(net)}</p>
                        ) : (
                          <p className="mt-1 text-stone-500 text-xs">Saisissez le coût d'abonnement pour estimer le ROI net.</p>
                        )}
                      </div>
                    );
                  })()}
                  <p className="text-[11px] text-amber-400/90 leading-snug">{bd.data.method}</p>
                </div>
              ) : null}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* NIVEAU 2 — SERVICE EN DIRECT */}
      <div className="rounded-2xl bg-[#111F1A] border border-[#1B3028] p-5" data-testid="live-service">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          <h3 className="text-sm font-bold uppercase tracking-wider text-stone-200">Service en direct</h3>
          <span className="text-[11px] text-stone-600">· temps réel</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <LiveTile testid="live-active-tables" dot="🟢" count={live.active_tables || 0} label="Tables actives" onClick={() => nav("tables")} />
          <LiveTile testid="live-preparing" dot="🟠" count={live.orders_preparing || 0} label="En préparation" onClick={() => nav("orders")} />
          <LiveTile testid="live-late" dot="🔴" count={live.orders_late || 0} label="Commandes en retard" urgent onClick={() => nav("orders")} />
          <LiveTile testid="live-waiter" dot="🟡" count={live.waiter_requests_pending || 0} label="Demandes serveur" onClick={() => openBreakdown("waiter_requests")} />
          <LiveTile testid="live-awaiting-payment" dot="💳" count={live.tables_awaiting_payment || 0} label="En attente de règlement" onClick={() => openBreakdown("pending_payments")} />
          <LiveTile testid="live-ready-close" dot="🪑" count={live.tables_ready_to_close || 0} label="Prêtes à clôturer" onClick={() => nav("tables")} />
        </div>
      </div>

      {/* NIVEAU 3 — ACTIVITÉ */}
      <div className="rounded-2xl bg-[#111F1A] border border-[#1B3028] p-5" data-testid="activity-chart">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-sm font-bold uppercase tracking-wider text-stone-200">Activité du restaurant · CA & commandes par heure</h3>
          {peak && (
            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#C69C4A] bg-[#C69C4A]/10 border border-[#C69C4A]/30 rounded-full px-3 py-1">
              <Flame className="w-3.5 h-3.5" /> Pic d'activité : {peak.from}h–{peak.to}h
            </span>
          )}
        </div>
        {activity.length === 0 ? (
          <div className="py-12 text-center text-stone-500 text-sm">{INSUFF}</div>
        ) : (
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <ComposedChart data={activity} margin={{ left: -10, right: 8, top: 8 }}>
                <CartesianGrid stroke="#1B3028" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: "#A8B0AB", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="left" tick={{ fill: "#A8B0AB", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: "#A8B0AB", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: "rgba(198,156,74,0.08)" }}
                  contentStyle={{ background: "#0A1712", border: "1px solid #1B3028", borderRadius: 12, color: "#fff" }}
                  formatter={(v, n) => (n === "revenue" ? [eur(v), "CA"] : [v, "Commandes"])}
                />
                <Bar yAxisId="left" dataKey="revenue" radius={[6, 6, 0, 0]} barSize={26}>
                  {activity.map((a, i) => <Cell key={i} fill={peak && a.hour === peak.from ? "#C69C4A" : "#1A3D32"} />)}
                </Bar>
                <Line yAxisId="right" type="monotone" dataKey="orders" stroke="#C69C4A" strokeWidth={2} dot={{ r: 3, fill: "#C69C4A" }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Top produits + Commandes par table (conservés) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-[#111F1A] border border-[#1B3028] p-5" data-testid="top-products">
          <h3 className="text-sm font-bold uppercase tracking-wider text-stone-300 mb-4">Produits les plus commandés</h3>
          {(!stats.top_products || stats.top_products.length === 0) ? (
            <div className="py-10 text-center text-stone-500 text-sm">{INSUFF}</div>
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={stats.top_products} layout="vertical" margin={{ left: 0, right: 16 }}>
                  <XAxis type="number" hide />
                  <YAxis type="category" dataKey="name" width={140} tick={{ fill: "#A8B0AB", fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "rgba(198,156,74,0.08)" }} contentStyle={{ background: "#0A1712", border: "1px solid #1B3028", borderRadius: 12, color: "#fff" }} formatter={(v) => [v, "Ventes"]} />
                  <Bar dataKey="quantity" radius={[0, 6, 6, 0]} barSize={18}>
                    {stats.top_products.map((_, i) => <Cell key={i} fill="#C69C4A" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="rounded-2xl bg-[#111F1A] border border-[#1B3028] p-5" data-testid="orders-by-table">
          <h3 className="text-sm font-bold uppercase tracking-wider text-stone-300 mb-4">Commandes par table</h3>
          {(!stats.orders_by_table || stats.orders_by_table.length === 0) ? (
            <div className="py-10 text-center text-stone-500 text-sm">{INSUFF}</div>
          ) : (
            <div className="space-y-2.5">
              {(() => { const max = Math.max(...stats.orders_by_table.map((r) => r.orders)); return stats.orders_by_table.map((row) => (
                  <div key={row.table} className="flex items-center gap-3">
                    <span className="text-sm text-stone-300 w-16 shrink-0">Table {row.table}</span>
                    <div className="flex-1 h-3 rounded-full bg-[#0A1712] overflow-hidden">
                      <div className="h-full bg-[#1A3D32]" style={{ width: `${(row.orders / max) * 100}%` }} />
                    </div>
                    <span className="text-xs text-stone-500 w-20 text-right">{eur(row.revenue)}</span>
                    <span className="text-sm font-bold text-[#C69C4A] w-6 text-right">{row.orders}</span>
                  </div>
              )); })()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
